<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
        <b-button
          class="left-right"
          type="is-success"
          icon-left="plus"
          :disabled="selectedPlant === null"
          @click="showCreateForm()"
        >
          Create New Inventory
        </b-button>
      </div>
      <div class="columns">
        <div class="column">
          <b-field grouped>
            <b-field label="Start Date">
              <b-datepicker
                v-model="start"
                name="startdate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="Start date"
              />
            </b-field>
            <b-field label="End Date">
              <b-datepicker
                v-model="end"
                name="enddate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="End date"
              />
            </b-field>
            <b-field v-if="hasGroupPlants" label="Plant">
              <b-select v-model="selectedPlant" icon="industry" expanded>
                <option v-for="plant in groupPlants" :key="plant.id" :value="plant">
                  {{ plant.name }}
                </option>
              </b-select>
            </b-field>
            <b-field v-else label="Plant">
              <multiselect
                v-model="selectedPlant"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
          </b-field>
          <b-field>
            <b-button
              class="left-right"
              type="is-warning"
              icon-left="arrows-rotate"
              :disabled="isLoading || selectedPlant === null"
              @click="() => {resetData(); getData();}"
            >
              Load
            </b-button>
          </b-field>
          <ag-grid-vue
            v-if="inventories.length != 0"
            style="width: 100%; height: 700px;"
            class="ag-theme-balham"
            :row-data="inventories"
            :grid-options="gridOptions"
            :pagination="true"
            :pagination-auto-page-size="true"
            :cache-overflow-size="0"
            :multi-sort-key="'ctrl'"
            :modules="modules"
            :get-context-menu-items="getContextMenuItems"
          />
        </div>
      </div>
    </div>
    <b-modal :active.sync="isCreatingInventory" scroll="keep" has-modal-card>
      <InventoryForm :plant="selectedPlant" :saving="isSaving" @submit="submitInventory" />
    </b-modal>
    <b-modal :active.sync="isEditingInventory" scroll="keep" has-modal-card>
      <form v-if="isEditingInventory" @submit.prevent="submitEdit">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit Inventory
              <span v-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Crate Type">
                <b-select v-model="editableInventory.type" placeholder="Crate Type">
                  <option value="1">
                    1
                  </option>
                  <option value="5.1">
                    5.1
                  </option>
                  <option value="5.2">
                    5.2
                  </option>
                  <option value="5.5">
                    5.5
                  </option>
                  <option value="7.1">
                    7.1
                  </option>
                  <option value="7.2">
                    7.2
                  </option>
                  <option value="LIDS">
                    LIDS
                  </option>
                  <option value="GPSFood">
                    GPS Food
                  </option>
                  <option value="ZEON">
                    ZEON
                  </option>
                  <option value="RHODIA">
                    RHODIA
                  </option>
                </b-select>
              </b-field>
              <b-field label="Status">
                <b-select v-model="editableInventory.status" placeholder="Status">
                  <option value="CLEAN">
                    CLEAN
                  </option>
                  <option value="DIRTY">
                    DIRTY
                  </option>
                  <option value="REPAIR">
                    REPAIR
                  </option>
                </b-select>
              </b-field>
              <b-field label="Date">
                <b-datepicker
                  v-model="editableInventory.date"
                  name="date"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                  placeholder="Date"
                />
              </b-field>
            </b-field>
            <b-field grouped>
              <b-field label="Number of Crates">
                <b-input v-model="editableInventory.num_crates" type="number" placeholder="Crates" />
              </b-field>
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { AllModules } from '@ag-grid-enterprise/all-modules'
import { mapState } from 'vuex'
import InventoryForm from './inventoryForm.vue'
import Inventory from '../../services/v3/crateInventory'
import plantSelector from '@/mixins/plantSelector'
import saving from '@/mixins/saving'
import { restoreConsultingPlant } from '@/helpers/plants'
// import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'

export default {
  name: 'CrateInventory',
  components: {
    InventoryForm
  },
  mixins: [plantSelector, saving],
  data: function () {
    return {
      start: null,
      end: new Date(),
      selectedPlant: restoreConsultingPlant(),
      isCreatingInventory: false,
      isEditingInventory: false,
      isLoading: false,
      editableInventory: null,
      inventories: [],
      modules: AllModules,
      gridOptions: {
        cacheBlockSize: 20,
        paginationPageSize: 20,
        enableRangeSelection: true,
        defaultColDef: {
          floatingFilter: true,
          enableValue: true,
          enableRowGroup: true,
          enablePivot: false,
          resizable: true
        },
        columnDefs: [
          { headerName: 'Crates',
            field: 'num_crates',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Crate Type',
            field: 'type',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Status',
            field: 'status',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Date',
            field: 'date',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            sort: 'Desc',
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Estimated Stock',
            field: 'stock',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            sort: 'Desc',
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Discrepancy',
            field: 'discrepancy',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            sort: 'Desc',
            filterParams: { suppressAndOrCondition: true }
          }
        ],
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                // tool panel columns won't move when columns are reordered in the grid
                suppressSyncLayoutWithGrid: true,
                // prevents columns being reordered from the Columns Tool Panel
                suppressColumnMove: true
              }
            }
          ],
          defaultToolPanel: ''
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    })
  },
  mounted: async function () {
    this.start = new Date()
    this.start.setDate(new Date().getDate() - 1)
    await this.loadPlants()
  },
  methods: {
    resetData () {
      this.inventories = []
    },
    showCreateForm () {
      this.isCreatingInventory = true
    },
    showEditForm (inventory) {
      this.isEditingInventory = true
      this.editableInventory = inventory
      this.editableInventory.date = new Date(new Date(this.editableInventory.date).toISOString().split('T')[0] + 'T00:00:00')
    },
    async getData () {
      sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      try {
        this.isLoading = true
        const data = await Inventory.getTable({
          start: this.start.toLocaleDateString('en-CA'),
          end: this.end.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id
        })
        data.forEach(element => {
          this.inventories.push({
            id: element.id,
            id_site: element.id_site,
            num_crates: element.num_crates,
            type: element.status_crates.split('__')[0],
            status: element.status_crates.split('__')[1],
            status_crates: element.status_crates,
            date: new Date(element.date.split('Z')[0]).toLocaleDateString('en-CA'),
            stock: element.stock[0].num_crates,
            discrepancy: element.num_crates - element.stock[0].num_crates
          })
        })
        if (this.inventories.length > 0) {
          this.$buefy.toast.open({ message: 'Data loaded', duration: 3000, type: 'is-success' })
        } else {
          this.$buefy.toast.open({ message: 'No corresponding Inventory found', duration: 3000, type: 'is-danger' })
        }
      } catch (error) {
        this.isLoading = false
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
      this.isLoading = false
    },
    getContextMenuItems (params) {
      const selectedInventory = params.node.data
      let customMenu = []
      const genericMenu = [
        'separator',
        'copy',
        'copyWithHeaders',
        'separator',
        'export'
      ]
      customMenu.push({
        name: 'Edit Inventory',
        action: () => {
          this.showEditForm(selectedInventory)
        },
        icon: '<i class="fas fa-fw fa-edit" ></i>'
      })
      customMenu.push({
        name: 'Delete Inventory',
        action: () => {
          this.deleteInventory(selectedInventory)
        },
        icon: '<i class="fas fa-fw fa-xmark" ></i>'
      })
      const result = [...customMenu, ...genericMenu]
      return result
    },
    // Every count goes in one request, saved whole or not at all.
    async submitInventory (inventory) {
      if (!await this.runSave(() => Inventory.createInventory(inventory))) {
        return
      }
      this.isCreatingInventory = false
      this.$buefy.toast.open({ message: 'Inventory(s) saved', duration: 3000, type: 'is-success' })
      this.resetData()
      this.getData()
    },
    async submitEdit () {
      this.isEditingInventory = false
      this.editableInventory.status_crates = this.editableInventory.type + '__' + this.editableInventory.status
      this.editableInventory.date = this.editableInventory.date.toLocaleDateString('en-CA')
      try {
        await Inventory.updateInventory(this.editableInventory)
        this.$buefy.toast.open({ message: 'Inventory updated', duration: 3000, type: 'is-success' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    },
    async deleteInventory (inventory) {
      try {
        await Inventory.archiveInventory(inventory.id)
        this.$buefy.toast.open({ message: 'Inventory Deleted', duration: 3000, type: 'is-warning' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    }
  }
}

</script>
